import classNames from 'classnames';
import type { ParsedQs } from 'qs';
import { parse, stringify } from 'qs';
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  KeyboardEvent,
  ReactNode,
  RefObject,
} from 'react';
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, resolvePath, useLocation, type To } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { FullViewportScroll } from '#src/atoms/container/FullViewportScroll/index.tsx';
import { WithScrollButtons } from '#src/atoms/navigation/WithScrollButtons/index.tsx';
import { useIsVisibleInViewport } from '#src/hooks/ui/useIsVisibleInViewport.ts';
import { useEmitClickButton } from '#src/hooks/useEmitClickButton.ts';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';
import { hideScrollbar } from '#src/style/utils.tsx';

type LinkProps = ComponentPropsWithoutRef<typeof Link>;
type ContextProps = {
  value?: string | null;
  currentPathname: string;
  qsFromLocation: ParsedQs;
  onValueChange: (value?: string) => void;
  scrollableContainerRef?: RefObject<HTMLDivElement | null>;
  activeTabNode: HTMLElement | null;
  setActiveTabNode: (node: HTMLElement | null) => void;
};

const Context = createContext<ContextProps | undefined>(undefined);

function useTabBarContext(consumerName: string) {
  const context = useContext(Context);
  if (context) return context;
  throw new Error(`\`${consumerName}\` must be used within \`AnimatedTabBar\``);
}

const Wrapper = styled.div`
  --bar-padding: var(--half-unit);
  --bar-bg-color: rgba(var(--c-rgb-white), 0.075);
  --bar-border-bottom: none;
  --bar-border: none;
  --bar-border-radius: var(--intermediate-unit);
  --tab-padding-x: var(--double-unit);
  --tab-padding-y: var(--intermediate-unit);
  --fg-color: var(--c-white);
  --default-bg-color: transparent;
  --hover-fg-color: var(--c-white);
  --hover-bg-color: rgb(var(--c-rgb-white), 0.1);
  --active-bg-color: var(--c-white);
  --active-fg-color: var(--c-black);
  --active-badge-bg-color: var(--c-nd-700);
  --button-border-radius: var(--unit);
  --animated-border-bottom: none;

  ${hideScrollbar}
  position: relative;
  isolation: isolate;
  display: inline-block;
  &.primary {
    --tab-padding-x: var(--intermediate-unit);
    --tab-padding-y: calc(var(--half-unit) - 1px);
    --bar-bg-color: var(--c-black);
    --bar-border: 1px solid var(--c-nd-150);
  }
  &.secondary {
    --bar-bg-color: var(--c-nd-50);
    --fg-color: var(--c-nd-600);
    --hover-fg-color: var(--c-nd-600);
    --active-bg-color: var(--c-nd-200);
    --active-fg-color: var(--c-white);
    --active-badge-bg-color: var(--c-nd-400);
  }
  &.compact {
    --tab-padding-x: var(--intermediate-unit);
    --tab-padding-y: calc(var(--half-unit) - 1px);
    --bar-bg-color: var(--c-nd-50);
    --bar-border: 1px solid var(--c-nd-150);
  }
  &.segmented {
    --bar-padding: 0;
    --tab-padding-x: var(--unit);
    --tab-padding-y: var(--half-unit);
    --bar-bg-color: transparent;
    --fg-color: var(--c-white);
    --active-bg-color: var(--c-white);
    --active-fg-color: var(--c-black);
    --active-button-border: 1px solid var(--c-white);
    --button-border: 1px solid var(--c-nd-150);

    &.compact {
      --bar-border: none;
    }
  }
  &.flat {
    --bar-bg-color: var(--bar-bg-color-custom, var(--c-black));
    --hover-bg-color: transparent;
    --active-bg-color: transparent;
    --fg-color: var(--c-nd-600);
    --hover-fg-color: var(--c-white);
    --active-fg-color: var(--c-white);
    --bar-padding: 0;
    --button-border-radius: 0;
    --animated-border-bottom: 2px solid var(--c-white);
    --bar-border-bottom: 1px solid var(--c-nd-100);
    --bar-border-radius: 0;
  }
  &.center ul {
    justify-content: center;
  }
  &.fullWidth {
    flex: 1;
    --tab-padding-x: var(--intermediate-unit);
    & li {
      flex: 1 1 0%;
    }
    &:not(.forceMobileLayout) {
      @media ${tabletAndAbove} {
        & > ul {
          padding-left: var(--double-unit);
        }
      }
      & li {
        @media ${tabletAndAbove} {
          flex: 0 0 auto;
        }
      }
    }
  }

  &.transparent {
    --bar-bg-color: transparent;
  }
`;

type Props = {
  children?: ReactNode;
  className?: string;
  value?: string | null;
  compact?: boolean;
  fullWidth?: boolean;
  scrollable?: boolean;
  variant?: 'primary' | 'secondary' | 'flat' | 'segmented';
  ignoreQueryParams?: boolean;
  onValueChange?: (value: string) => void | Promise<void>;
  transparent?: boolean;
  scrollableContainerRef?: RefObject<HTMLDivElement | null>;
  forceMobileLayout?: boolean;
  hideBackgroundWhenNoValue?: boolean;
  center?: boolean;
  grow?: boolean;
  defaultValue?: string | null;
};

const Root = ({
  value: valueProp,
  variant = 'primary',
  compact,
  className,
  onValueChange: onValueChangeProp,
  fullWidth,
  ignoreQueryParams,
  transparent,
  scrollableContainerRef,
  forceMobileLayout,
  center,
  defaultValue,
  ...divProps
}: Props) => {
  const [value, setValue] = useState(valueProp || defaultValue);
  const [activeTabNode, setActiveTabNode] = useState<HTMLElement | null>(null);

  const location = useLocation();
  const qsFromLocation = ignoreQueryParams
    ? {}
    : parse(location.search, {
        ignoreQueryPrefix: true,
      });
  const { pathname: currentPathname = '' } = location;

  const onValueChange = useCallback(
    (change?: string) => {
      if (onValueChangeProp) {
        if (change !== undefined) {
          onValueChangeProp(change);
        }
      } else {
        setValue(change);
      }
    },
    [onValueChangeProp]
  );

  return (
    <Context.Provider
      value={{
        value: valueProp ?? value,
        qsFromLocation,
        currentPathname,
        onValueChange,
        scrollableContainerRef,
        activeTabNode,
        setActiveTabNode,
      }}
    >
      <Wrapper
        className={classNames(className, variant, {
          compact,
          fullWidth,
          transparent,
          forceMobileLayout,
          center,
        })}
        {...divProps}
      />
    </Context.Provider>
  );
};

const Bar = styled.ul.attrs({
  role: 'tablist',
  'aria-orientation': 'horizontal',
})`
  display: flex;
  align-items: center;
  gap: var(--bar-gap-custom, var(--half-unit));
  margin: 0;
  padding: var(--bar-padding);
  background-color: var(--bar-bg-color);
  border-radius: var(--bar-border-radius);
  border-bottom: var(--bar-border-bottom);
  .compact &,
  .primary & {
    border: var(--bar-border);
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--double-unit);
  padding: var(--half-unit) var(--unit);
  line-height: var(--double-unit);
  text-align: center;
  font-weight: var(--t-bold);
  white-space: nowrap;
  min-width: 1.5em;
  background-color: var(--bar-bg-color);
  color: var(--fg-color);
  &:first-child {
    margin-inline-start: calc(-1 * var(--unit));
  }
  &:last-child {
    margin-inline-end: calc(-1 * var(--unit));
  }
`;

const Button = styled(ButtonBase)`
  position: relative;
  width: 100%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--unit);
  padding: var(--tab-padding-y) var(--tab-padding-x);
  font: var(--t-bold) var(--t-16);
  .compact & {
    font: var(--t-bold) var(--t-label-m);
  }
  color: var(--fg-color);
  text-align: center;
  border-radius: var(--button-border-radius);
  border: var(--button-border);
  scroll-snap-align: start;
  scroll-margin: var(--unit);
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.2s ease-in-out,
    background-color 0.1s ease-in-out,
    border-color 0.1s ease-in-out;
  & img {
    transition: opacity 0.2s ease-in-out;
    opacity: 0.5;
  }
  &:not(.active):hover,
  &:not(.active):focus {
    color: var(--hover-fg-color);
    background-color: var(--hover-bg-color);
    & img {
      opacity: 1;
    }
  }
  &:not(.active) {
    background-color: var(--default-bg-color, transparent);
  }
  &.active {
    color: var(--active-fg-color);
    border: var(--active-button-border);
    cursor: default;
    & ${Badge} {
      color: var(--active-fg-color);
      background-color: var(--active-badge-bg-color);
      box-shadow: var(--shadow-100);
    }
    & img {
      opacity: 1;
    }
  }
  &.active,
  &.disabled {
    pointer-events: none;
  }
  &.disabled {
    opacity: 0.4;
  }
`;

type TabProps = {
  value?: string;
  to?: To;
  matches?: (string | undefined | null | false)[];
} & Partial<
  Omit<LinkProps, 'to' | 'keepSearchParams'> &
    ButtonHTMLAttributes<HTMLButtonElement>
>;

function getActiveAndPath(
  currentPathname: string,
  to: To,
  qsFromLocation: ParsedQs,
  matches?: (string | undefined | null | false)[]
) {
  const { pathname = '', search } = resolvePath(to);
  const qsFromTo = parse(search || '', {
    ignoreQueryPrefix: true,
  });
  const pathMatch = matchPath(pathname, currentPathname);
  const match =
    Boolean(pathMatch) ||
    matches?.some(
      m =>
        m &&
        (matchPath(m, currentPathname) || matchAnySportPath(m, currentPathname))
    );
  const path =
    pathname +
    stringify(
      Object.fromEntries(
        Object.entries({ ...qsFromLocation, ...qsFromTo }).filter(([, v]) =>
          Boolean(v)
        )
      ),
      { addQueryPrefix: true }
    );
  return [Boolean(match), path] as const;
}

export const Tab = ({
  value,
  to,
  matches,
  disabled,
  replace = true,
  className,
  onClick,
  ...props
}: TabProps) => {
  const {
    value: barValue,
    currentPathname,
    qsFromLocation,
    onValueChange,
    scrollableContainerRef,
    activeTabNode,
    setActiveTabNode,
  } = useTabBarContext('Tab');
  const emitClickButton = useEmitClickButton();
  const { handleRef, node, isVisible } = useIsVisibleInViewport<
    HTMLAnchorElement | HTMLButtonElement
  >();

  const scrollHandled = useRef(false);
  const [active, path] = useMemo((): readonly [boolean, string?] => {
    if (!to) return [value === barValue];
    const pair = getActiveAndPath(currentPathname, to, qsFromLocation, matches);
    return value === undefined ? pair : [value === barValue, pair[1]];
  }, [to, value, barValue, currentPathname, qsFromLocation, matches]);

  useEffect(() => {
    if (active && node && node !== activeTabNode) {
      setActiveTabNode(node);
    }
  }, [active, activeTabNode, node, setActiveTabNode]);

  useEffect(() => {
    if (
      active &&
      node &&
      scrollableContainerRef?.current &&
      isVisible !== undefined &&
      scrollHandled.current === null
    ) {
      scrollHandled.current = true;
      if (!isVisible) {
        // We need to use Math.round on scrollLeft as this value can be a float
        // Whereas offsetLeft is rounded to an integer
        // This mismatch causes scrolling when none is needed
        const offset =
          node.offsetLeft -
          Math.round(scrollableContainerRef.current.scrollLeft);
        scrollableContainerRef.current.scrollBy?.({
          left: offset,
          behavior: 'smooth',
        });
      }
    }
  }, [active, node, scrollableContainerRef, isVisible]);

  return (
    <li className={className}>
      <Button
        ref={handleRef}
        as={to ? Link : 'button'}
        type={to ? undefined : 'button'}
        to={path}
        replace={replace}
        role="tab"
        aria-selected={active}
        className={classNames({ active, disabled })}
        {...props}
        onClick={(event: any) => {
          onValueChange(value);
          onClick?.(event);
          emitClickButton();
        }}
        onKeyDown={(event: KeyboardEvent) => {
          if (value !== undefined && [' ', 'Enter'].includes(event.key)) {
            onValueChange(value);
          }
        }}
      />
    </li>
  );
};

const SeparatorWrapper = styled.div`
  margin: 0 var(--half-unit);
`;

export const Separator = ({ className }: { className?: string }) => {
  return (
    <SeparatorWrapper className={className}>
      <svg width="1" height="24">
        <rect width="1" height="24" fill="var(--c-nd-200)" />
      </svg>
    </SeparatorWrapper>
  );
};

const ActiveDiv = styled.div`
  position: absolute;
  top: var(--bar-padding);
  bottom: var(--bar-padding);
  left: 0;
  width: 0;
  z-index: 1;
  background: var(--active-bg-color);
  border-radius: var(--button-border-radius);
  border-bottom: var(--animated-border-bottom);
  /** use CSS animation rather than react-spring for this; lighter */
  transition:
    left 0.3s ease-in-out,
    width 0.3s ease-in-out;
  will-change: left, width;

  .segmented & {
    transition: unset;
  }
`;

type ActiveBackgroundProps = {
  className?: string;
};

const ActiveBackground = (props: ActiveBackgroundProps) => {
  const context = useTabBarContext('ActiveBackground');
  const { activeTabNode } = context;
  if (!activeTabNode) return null;

  const { offsetLeft: left } = activeTabNode;
  const { width } = activeTabNode.getBoundingClientRect();

  return <ActiveDiv style={{ left, width }} {...props} />;
};

export const TabBar = ({
  children,
  scrollable = true,
  hideBackgroundWhenNoValue = false,
  value,
  fullWidth,
  grow,
  ...props
}: Props) => {
  const wrapperRef = useRef(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const ScrollableWrapper = scrollable ? FullViewportScroll : Fragment;
  return (
    <WithScrollButtons
      contained
      wrapperRef={wrapperRef}
      scrollContainerRef={scrollableContainerRef}
      hideButtonsOnMobile
      grow={grow}
    >
      <ScrollableWrapper ref={scrollable ? scrollableContainerRef : undefined}>
        <Root
          value={value}
          fullWidth={fullWidth}
          grow={grow}
          {...props}
          scrollableContainerRef={scrollableContainerRef}
        >
          <Bar>{children}</Bar>
          {(!!value || !hideBackgroundWhenNoValue) && <ActiveBackground />}
        </Root>
      </ScrollableWrapper>
    </WithScrollButtons>
  );
};
